import { useCallback, useReducer } from "react";
import { useProfile } from "@/context/ProfileContext";
import { trpcClient } from "@/trpc/client";
import type { ProductKey } from "@/utils/productAccess";

/**
 * Wizards completed in this browser session. Module-level because each route
 * guard mounts its own gate instance and the profile snapshot is not a live
 * listener — without this, finishing the wizard on one route would re-show
 * it when navigating to a sibling route before the profile refreshes.
 */
const completedThisSession = new Set<string>();

/**
 * Legacy-data heuristics: businesses that configured a product before
 * first-run wizards existed must not be nagged. A product counts as already
 * configured when its profile sections were completed the old way.
 * (Intelligence has no profile section — its gate checks for existing teams.)
 */
function hasLegacyConfig(
  completedSteps: string[],
  product: ProductKey,
): boolean {
  switch (product) {
    case "recruit":
      return (
        completedSteps.includes("compensation") ||
        completedSteps.includes("hiring-plan")
      );
    case "leads":
      return completedSteps.includes("leads-config");
    case "intelligence":
      return false;
  }
}

export interface ProductFirstRunState {
  /** True while profile data is still resolving — render nothing special. */
  loading: boolean;
  /**
   * True when this member should see the product's first-run wizard.
   * Callers gate on product access separately (route guards already do).
   */
  needsFirstRun: boolean;
  /** Marks the wizard done (or skipped) and hides it for good. */
  complete: (opts?: { skipped?: boolean }) => Promise<void>;
}

export function useProductFirstRun(product: ProductKey): ProductFirstRunState {
  const { businessProfile, memberAccessLevel, loading, refreshProfile } =
    useProfile();
  const [, forceRender] = useReducer((n: number) => n + 1, 0);

  const businessId = businessProfile?.uid ?? null;
  const sessionKey = `${businessId}:${product}`;

  const complete = useCallback(
    async (opts?: { skipped?: boolean }) => {
      // Hide immediately across every gate instance in this session.
      completedThisSession.add(sessionKey);
      forceRender();
      if (!businessId) return;
      try {
        await trpcClient.onboarding.markProductOnboardingComplete.mutate({
          businessId,
          product,
          skipped: opts?.skipped ?? false,
        });
        // Pull the updated business doc so the durable state takes over.
        await refreshProfile();
      } catch {
        // Non-fatal: worst case the wizard reappears on next visit.
      }
    },
    [businessId, product, sessionKey, refreshProfile],
  );

  const completedSteps =
    businessProfile?.companyProfile?.completedSteps ?? [];

  const needsFirstRun =
    !loading &&
    businessProfile != null &&
    !completedThisSession.has(sessionKey) &&
    // Team-scoped members cannot complete org-wide configuration.
    memberAccessLevel === "full" &&
    businessProfile.productOnboarding?.[product] == null &&
    !hasLegacyConfig(completedSteps, product);

  return { loading, needsFirstRun, complete };
}
